import dayjs from 'dayjs';

import { chains } from 'src/assets/data/chains';
import {
  RebalanceFilters,
  RebalanceItem,
} from 'src/pages/admin/services/xbitRelayRebalancesService';

import { REBALANCE_APPROVABLE_STATUSES, REBALANCE_TERMINAL_STATUSES } from './constants';

export type RebalanceFilterForm = {
  provider?: string;
  status?: string;
};

export const pickRebalanceFilters = (filter?: RebalanceFilterForm): RebalanceFilters => {
  if (!filter) return {};
  const out: RebalanceFilters = {};
  if (filter.provider) out.provider = filter.provider;
  // The BE matches `status` exactly, so a stray space would silently return nothing.
  const status = filter.status?.trim();
  if (status) out.status = status;
  return out;
};

/**
 * int64-as-string timestamps. `'0'` is the BE's "not set yet" sentinel for `approvedAtUnixMs`
 * (and epoch-0 is never a real value here), so it renders as `-` rather than 1970.
 */
export const formatUnixMs = (
  value?: string | number | null,
  pattern = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (value === undefined || value === null || value === '') return '-';
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms <= 0) return '-';
  return dayjs(ms).format(pattern);
};

/** Amounts are decimal strings and there is no decimal lib here — never go through parseFloat. */
export const formatAmount = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  return trimmed || '0';
};

export const normalizeStatus = (status?: string | null): string =>
  (status ?? '').trim().toUpperCase();

export const isApprovableRebalance = (status?: string | null): boolean =>
  (REBALANCE_APPROVABLE_STATUSES as readonly string[]).includes(normalizeStatus(status));

export const isTerminalRebalance = (status?: string | null): boolean =>
  (REBALANCE_TERMINAL_STATUSES as readonly string[]).includes(normalizeStatus(status));

export const canApproveRebalance = (status?: string | null): boolean =>
  isApprovableRebalance(status);

export const canRejectRebalance = (status?: string | null): boolean =>
  isApprovableRebalance(status);

/**
 * In flight = past the approval gate but not yet closed out, which includes `STUCK` — that is
 * precisely the state an operator resolves. An unknown status counts as in flight so a new
 * lifecycle value never strands a row with no action.
 */
export const canResolveRebalance = (status?: string | null): boolean => {
  const normalized = normalizeStatus(status);
  if (!normalized) return false;
  return !isApprovableRebalance(normalized) && !isTerminalRebalance(normalized);
};

export const statusChipColor = (
  status?: string | null
): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const colors: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
    PROPOSED: 'warning',
    PENDING: 'warning',
    APPROVED: 'info',
    SIGNED: 'info',
    BROADCAST_SRC: 'info',
    RELAY_PENDING: 'info',
    CONFIRMED_DST: 'info',
    DONE: 'success',
    REJECTED: 'error',
    FAILED: 'error',
    STUCK: 'error',
  };
  return colors[normalizeStatus(status)] ?? 'default';
};

const findChain = (chainId?: string | number | null) => {
  const id = Number(chainId);
  return Number.isFinite(id) ? chains.find((chain) => chain.id === id) : undefined;
};

/** Falls back to the raw chain id so an unmapped chain still renders something useful. */
export const chainLabel = (chainId?: string | number | null): string => {
  if (chainId === undefined || chainId === null || chainId === '') return '-';
  return findChain(chainId)?.name ?? String(chainId);
};

export const explorerTxUrl = (chainId?: string | number | null, txHash?: string | null): string => {
  if (!txHash) return '';
  const chain = findChain(chainId);
  return chain ? `${chain.browser}${txHash}` : '';
};

export const truncateMiddle = (value?: string | null, head = 8, tail = 6): string =>
  value && value.length > head + tail + 3
    ? `${value.slice(0, head)}...${value.slice(-tail)}`
    : value || '-';

/** These services use the bare axios instance, so failures carry `response.data`. */
type AxiosLikeError = { response?: { data?: { message?: string } }; message?: string };

export const errorMessage = (error: AxiosLikeError | undefined, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

/** A wallet popup the operator dismissed is not an error worth notifying about. */
export const isUserRejection = (error?: {
  code?: number | string;
  cause?: { code?: number | string };
  message?: string;
}): boolean => {
  const code = error?.code ?? error?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED') return true;
  const message = (error?.message || '').toLowerCase();
  return message.includes('reject') || message.includes('denied');
};

type TokenPair = Pick<RebalanceItem, 'srcTokenSymbol' | 'dstTokenSymbol'>;

export const sourceTokenSymbol = (record: TokenPair): string =>
  (record.srcTokenSymbol ?? '').trim();

/**
 * Falls back to the source symbol when `dstTokenSymbol` is absent, so a row that arrives without
 * it renders as a plain same-token move rather than a blank.
 */
export const destinationTokenSymbol = (record: TokenPair): string =>
  (record.dstTokenSymbol ?? '').trim() || sourceTokenSymbol(record);

/**
 * A rebalance can swap as well as bridge. When it does, `amount` is denominated in the source
 * token and `minReceived` in the destination one — so the two symbols have to be shown apart.
 */
export const isTokenSwap = (record: TokenPair): boolean => {
  const from = sourceTokenSymbol(record).toUpperCase();
  const to = destinationTokenSymbol(record).toUpperCase();
  return Boolean(from) && Boolean(to) && from !== to;
};

/**
 * The row a resolve dialog was opened on may have moved on between render and signing, so the
 * signed `expected_amount` is always read back off the record at submit time.
 */
export const expectedAmountOf = (record: Pick<RebalanceItem, 'amount'>): string =>
  (record.amount ?? '').trim();
